import { initEvents, DataEvent } from "@adfenix/ui-events";
import { sendUserEvents } from "api/services/customerPortalService";
import { produce } from "immer";

const guidRegex =
  /([0-9A-Fa-f]{8}[-]?[0-9A-Fa-f]{4}[-]?[0-9A-Fa-f]{4}[-]?[0-9A-Fa-f]{4}[-]?[0-9A-Fa-f]{12})/g;

/**
 * Adds a viewName property to the metadata if the original view contains unique identifiers.
 * Remove all UUIDS from the path.
 */
const pathParamReplacer = (
  event: DataEvent,
  metaData: Record<string, string>
) => {
  const viewName = event.view.replaceAll(guidRegex, ":id");
  return produce(metaData, draft => {
    draft.viewName = viewName;
  });
};

const pathParamEntityExtractor = (
  event: DataEvent,
  metaData: Record<string, string>
) => {
  const matches = event.view.match(guidRegex);

  if (!matches) {
    return metaData;
  }

  return produce(metaData, draft => {
    draft.entityId = matches.at(-1) ?? "";
  });
};

/**
 * Appends entity identifiers to simplify usage in the BI models.
 * programInstanceId is otherwise available in the query string and that is inconvenient.
 */
const queryParamEntityExtractor = (
  event: DataEvent,
  metaData: Record<string, string>
) => {
  const params = new URLSearchParams(event.search);

  if (!params.has("programInstanceId")) {
    return metaData;
  }

  return produce(metaData, draft => {
    draft.entityId = params.get("programInstanceId")!;
  });
};

const metaDataMappers: ((
  e: DataEvent,
  metaData: Record<string, string>
) => Record<string, string>)[] = [
  pathParamReplacer,
  pathParamEntityExtractor,
  queryParamEntityExtractor
];

export const getCustomMetaData = (event: DataEvent) => {
  try {
    return metaDataMappers.reduce(
      (metaData, mapper) => mapper(event, metaData),
      event.metaData
    );
  } catch (e) {
    return event.metaData;
  }
};

const uiEvents = initEvents({
  flushInterval: 10000,
  labelAttribute: "data-loglabel",
  request: sendUserEvents,
  debug: false,
  getCustomMetaData,
  window
});

export default Object.seal(uiEvents);
